import { FieldWrapper } from '@/components/Form/FieldWrapper';
import { type Option, SearchField, SelectField } from '@/components/Form';
import { Button } from '@/components/Elements';
import { rwColors } from '@/styles/colors';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
} from '@heroicons/react/24/solid';
import {
  FragmentOf,
  graphql,
  readFragment,
  useQuery,
  VariablesOf,
} from '@/lib/graphql';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type StylesConfig } from 'react-select';
import { type TFunction } from 'i18next';
import {
  type FilterFormState,
  type MultiSelectFieldKey,
  type OrganizationSearchCondition,
} from '../types';

// === config & styles ===
const SELECT_FIELD_CONFIGS = [
  {
    key: 'types',
    labelKey: 'filter.label.organizationType',
    placeholderKey: 'filter.placeholder.organizationType',
  },
  {
    key: 'countryTags',
    labelKey: 'filter.label.regionEconomicUnion',
    placeholderKey: 'filter.placeholder.regionEconomicUnion',
  },
  {
    key: 'countries',
    labelKey: 'filter.label.countryRegionalOrganization',
    placeholderKey: 'filter.placeholder.countryRegionalOrganization',
  },
  {
    key: 'statuses',
    labelKey: 'filter.label.publicationStatus',
    placeholderKey: 'filter.placeholder.publicationStatus',
  },
] as const;

type SelectFieldConfig = (typeof SELECT_FIELD_CONFIGS)[number];

const selectFieldStyles: StylesConfig<Option, true> = {
  control: (provided) => ({ ...provided, borderWidth: '2px' }),
  placeholder: (provided) => ({ ...provided, color: rwColors.gray }),
};

// === data fetching & helpers ===
const filterSectionFacetOptionFragment = graphql(`
  fragment FilterSectionFacetOptionFragment on SelectOption {
    value
    count
  }
`);

const ORGANIZATION_FACETS_QUERY = graphql(
  `
    query OrganizationFacets(
      $condition: OrganizationSearchConditionInput
      $facetKey: OrganizationFacetKey!
    ) {
      organizationFacets(condition: $condition, facetKey: $facetKey) {
        ...FilterSectionFacetOptionFragment
      }
    }
  `,
  [filterSectionFacetOptionFragment]
);

const FACET_FIELD_TO_KEY_MAP = {
  types: 'type',
  statuses: 'status',
  countries: 'country',
  countryTags: 'country_tags',
} as const satisfies Record<
  MultiSelectFieldKey,
  VariablesOf<typeof ORGANIZATION_FACETS_QUERY>['facetKey']
>;

const PRIORITIZED_OPTION_BY_FIELD: Partial<
  Record<MultiSelectFieldKey, string>
> = {
  countries: 'Multinational',
  countryTags: 'Global',
};

const mapFacetToOptions = (
  facets?: readonly FragmentOf<typeof filterSectionFacetOptionFragment>[]
) => {
  const normalized = facets ?? [];

  return normalized
    .map((facet) => readFragment(filterSectionFacetOptionFragment, facet))
    .map(({ value }) => value)
    .filter((value): value is string => Boolean(value))
    .map<Option>((value) => ({ value, label: value }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const mergeSelections = (options: Option[], selected: Option[]) => {
  if (selected.length === 0) return options;

  const merged = new Map(options.map((option) => [option.value, option]));
  selected.forEach((option) => {
    if (!merged.has(option.value)) merged.set(option.value, option);
  });

  return Array.from(merged.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

const buildOptionsForField = (
  field: MultiSelectFieldKey,
  facets: readonly FragmentOf<typeof filterSectionFacetOptionFragment>[] = [],
  selected: Option[]
) => {
  const merged = mergeSelections(mapFacetToOptions(facets), selected);
  const priority = PRIORITIZED_OPTION_BY_FIELD[field];
  if (!priority) return merged;

  const priorityIndex = merged.findIndex((option) => option.value === priority);
  if (priorityIndex <= 0) return merged;

  const prioritized = merged[priorityIndex];
  return [
    prioritized,
    ...merged.slice(0, priorityIndex),
    ...merged.slice(priorityIndex + 1),
  ];
};

const hasAnyFilter = (state: FilterFormState) =>
  Boolean(state.query.trim()) ||
  state.types.length > 0 ||
  state.statuses.length > 0 ||
  state.countries.length > 0 ||
  state.countryTags.length > 0;

const useOrganizationFacetOptions = ({
  field,
  condition,
  enabled,
}: {
  field: MultiSelectFieldKey;
  condition?: OrganizationSearchCondition;
  enabled: boolean;
}) => {
  const facetKey = FACET_FIELD_TO_KEY_MAP[field];
  const { data, loading } = useQuery(ORGANIZATION_FACETS_QUERY, {
    variables: { condition, facetKey },
    skip: !enabled,
    notifyOnNetworkStatusChange: true,
  });

  return {
    facets: data?.organizationFacets ?? [],
    isLoading: loading,
  } as const;
};

type FilterSectionProps = {
  formState: FilterFormState;
  appliedFilters: FilterFormState;
  isExpanded: boolean;
  condition?: OrganizationSearchCondition;
  onToggle: () => void;
  onSearch: (value: string | undefined) => void;
  onQueryChange: (value: string) => void;
  onSelectChange: (
    field: MultiSelectFieldKey
  ) => (options: readonly Option[] | null) => void;
  onReset: () => void;
};

export const FilterSection = ({
  formState,
  appliedFilters,
  isExpanded,
  condition,
  onToggle,
  onSearch,
  onQueryChange,
  onSelectChange,
  onReset,
}: FilterSectionProps) => {
  const { t } = useTranslation();
  const hasFormFilters = hasAnyFilter(formState);
  const hasAppliedFilters = hasAnyFilter(appliedFilters);
  const isResetDisabled = !(hasFormFilters || hasAppliedFilters);

  return (
    <div className="mb-6 rounded-sm rounded-tr-xl border-2 border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-sm rounded-tr-xl px-4 py-2.5 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <FunnelIcon className="size-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">
            {t('フィルター')}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="size-4 text-gray-600" />
        ) : (
          <ChevronDownIcon className="size-4 text-gray-600" />
        )}
      </button>

      <div
        className={clsx(
          'transition-all duration-300 ease-in-out',
          isExpanded
            ? 'max-h-screen overflow-visible opacity-100'
            : 'max-h-0 overflow-hidden opacity-0'
        )}
      >
        <div className="border-t border-gray-100 p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex-1">
                <SearchField
                  value={formState.query}
                  onSearch={onSearch}
                  onValueChange={onQueryChange}
                />
              </div>
            </div>

            <FacetSelectGrid
              formState={formState}
              appliedFilters={appliedFilters}
              onSelectChange={onSelectChange}
              condition={condition}
              translate={t}
            />

            <div className="flex justify-end">
              <Button
                color="inverse"
                size="sm"
                className="sm:w-auto"
                onClick={onReset}
                disabled={isResetDisabled}
              >
                {t('リセット')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FilterSection.fragments = {
  organizationFacetOption: filterSectionFacetOptionFragment,
};

// === sub components ===
const FacetSelectGrid = ({
  formState,
  appliedFilters,
  onSelectChange,
  condition,
  translate,
}: {
  formState: FilterFormState;
  appliedFilters: FilterFormState;
  onSelectChange: (
    field: MultiSelectFieldKey
  ) => (options: readonly Option[] | null) => void;
  condition?: OrganizationSearchCondition;
  translate: TFunction;
}) => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    {SELECT_FIELD_CONFIGS.map((config) => (
      <OrganizationFacetSelectField
        key={config.key}
        config={config}
        formState={formState}
        appliedFilters={appliedFilters}
        onSelectChange={onSelectChange}
        condition={condition}
        translate={translate}
      />
    ))}
  </div>
);

const OrganizationFacetSelectField = ({
  config,
  formState,
  appliedFilters,
  onSelectChange,
  condition,
  translate,
}: {
  config: SelectFieldConfig;
  formState: FilterFormState;
  appliedFilters: FilterFormState;
  onSelectChange: (
    field: MultiSelectFieldKey
  ) => (options: readonly Option[] | null) => void;
  condition?: OrganizationSearchCondition;
  translate: TFunction;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { facets, isLoading } = useOrganizationFacetOptions({
    field: config.key,
    condition,
    enabled: isMenuOpen,
  });

  const options = buildOptionsForField(
    config.key,
    facets,
    appliedFilters[config.key]
  );

  return (
    <FieldWrapper label={translate(config.labelKey)}>
      <SelectField<Option, true>
        value={formState[config.key]}
        onChange={onSelectChange(config.key)}
        options={options}
        isMulti
        isClearable
        isSearchable
        placeholder={translate(config.placeholderKey)}
        styles={selectFieldStyles}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        isLoading={isLoading}
      />
    </FieldWrapper>
  );
};
